import { InfiniteData, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CHANNEL_DEFAULT_PAGE_SIZE, CHANNEL_DEFAULT_SORT } from '../../defaults';
import { ApActionType } from '../../enums/ApActionType';
import { ChannelItemDirection } from '../../enums/ChannelItemDirection';
import { ChannelItemStatus } from '../../enums/ChannelItemStatus';
import { ChannelItemType } from '../../enums/ChannelItemType';
import { ChannelType } from '../../enums/ChannelType';
import { ChannelItemSms, ChannelItemUnion } from '../../types/ChannelItem';
import { requestCreateDirectApPlan, requestTimeline } from '../Timeline/timeline';
import { ChannelItemsRequest } from '../Timeline/types/channelItemsRequest';
import { ChannelItemsResponse } from '../Timeline/types/channelItemsResponse';
import { createApPlanRequest } from '../Timeline/types/createApPlanRequest';

export function useTimeline(contactId: number, channels: ChannelType[]) {
    return useInfiniteQuery<
        ChannelItemsResponse,
        Error,
        InfiniteData<ChannelItemsResponse, number>,
        readonly unknown[],
        number
    >({
        queryKey: [`contact-${contactId}`, 'timeline', channels],
        queryFn: async ({ signal, pageParam }) => {
            const data = {
                contactId: contactId,
                channels: channels,
                pageNum: pageParam || 0,
                pageSize: CHANNEL_DEFAULT_PAGE_SIZE,
                sortOrder: CHANNEL_DEFAULT_SORT,
            } as ChannelItemsRequest;
            const result = await requestTimeline(contactId, data, signal);
            return result;
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            if (!lastPage?.items.length) {
                return undefined;
            }
            return lastPage.pageNum + 1;
        },
        refetchInterval: 15000,
    });
}

export function useCreateScheduleMessage() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: createApPlanRequest) => requestCreateDirectApPlan(data.Item),
        onMutate: async (apPlan: createApPlanRequest) => {
            console.log('[useCreateScheduleMessage] onMutate', apPlan);
            await queryClient.cancelQueries({
                queryKey: [`contact-${apPlan.Item.contactId}`, 'timeline'],
            });
            const currentTimeline = queryClient.getQueryData<InfiniteData<ChannelItemsResponse>>([
                `contact-${apPlan.Item.contactId}`,
                'timeline',
            ]);
            if (currentTimeline) {
                const newItem = buildNewMessageItem(apPlan);

                if (newItem) {
                    //add the item to the first item in the first page
                    //also handle the case where their phones suck and havent loaded anything yet,
                    //but theyre trying to send a message anyways. (build a basic page object)
                    let newData: InfiniteData<ChannelItemsResponse>;
                    if (currentTimeline && currentTimeline.pages.length > 0) {
                        newData = {
                            ...currentTimeline,
                            pages: [
                                {
                                    ...currentTimeline.pages[0],
                                    items: [newItem, ...currentTimeline.pages[0].items],
                                },
                                ...currentTimeline.pages.slice(1),
                            ],
                        };
                    } else {
                        newData = {
                            pages: [
                                {
                                    contactId: apPlan.Item.contactId,
                                    items: [newItem],
                                    pageNum: 0,
                                },
                            ],
                            pageParams: [],
                        };
                    }

                    queryClient.setQueryData<InfiniteData<ChannelItemsResponse>>(
                        [`contact-${apPlan.Item.contactId}`, 'timeline'],
                        newData,
                    );
                }
            }
            return { currentTimeline, apPlan };
        },
        onError(error, variables, context) {
            console.log(
                '[useCreateScheduleMessage] onError:',
                error,
                'variables:',
                variables,
                'context:',
                context,
            );
        },
        onSuccess: (data, variables, context) => {},
        onSettled: (data, error, variables, context) => {
            //on success & error
            queryClient.invalidateQueries({
                queryKey: [`contact-${variables.Item.contactId}`, 'timeline'],
            });
            queryClient.invalidateQueries({ queryKey: ['newAndRecent'] });
        },
    });
}

function buildNewMessageItem(apPlan: createApPlanRequest): ChannelItemUnion | undefined {
    if (apPlan.Item.actionType === ApActionType.sms) {
        return {
            id: 0,
            contactId: apPlan.Item.contactId,
            type: ChannelType.messageEvents,
            date: new Date().toString(),
            channelId: 0,
            subType: ChannelItemType.message,
            direction: ChannelItemDirection.outgoing,
            imageUrl: '',
            message: apPlan.Item.data.body || '',
            userName: apPlan.userName,
            status: ChannelItemStatus.Sending,
            uuid: apPlan.Item.data.uuid,
            platform: 'sic',
            smsStatus: 'sending',
            messageSid: '',
            referenceNumber: '',
            contactReferenceNumber: '',
            statusMessage: 'sending',
            media: [],
            srContactPlanItemId: undefined,
        } as ChannelItemSms;
    } else if (apPlan.Item.actionType === ApActionType.email) {
        return {
            id: 0,
            contactId: apPlan.Item.contactId,
            type: ChannelType.gmailEvents,
            date: new Date().toString(),
            channelId: 0,
            subType: ChannelItemType.email,
            direction: ChannelItemDirection.outgoing,
            userName: apPlan.userName,
            status: ChannelItemStatus.Sending,
            uuid: apPlan.Item.data.uuid,
            statusMessage: 'sending',
            subject: apPlan.Item.data.subject || '',
            body: apPlan.Item.data.body || '',
            referenceEmail: '',
            contactReferenceEmail: '',
        } as ChannelItemUnion;
    }

    return undefined;
}
